import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Checkbox, Dropdown } from 'antd';
import moment from 'moment';

import { routeFileSelector } from '@/utilz';
import Button from '@/views/components/Buttons/Button';
import BatchLabel from '@/views/components/Labels/BatchLabel';
import PrimaryLabel from '@/views/components/Labels/PrimaryLabel';
import SecondaryLabel from '@/views/components/Labels/SecondaryLabel';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import ContentSpinner from '@/views/components/Spinners/ContentSpinner';

import { ReactComponent as MoreIcon } from '../../../../assets/icons/dots-v.svg';
import { SaversShipmentColumns } from './constants';

const dateFormatter = (cell) => {
  const newDate = cell && moment(cell).format('MMM D YYYY');
  return <span>{newDate}</span>;
};

const SaversShipmentGridView = ({
  handleLoadMore,
  isLoading,
  hasMore,
  shipmentList,
  handleShipmentClick,
  handleStatusUpdate,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const tableViewColumns = SaversShipmentColumns;

  const getMenu = (row) => {
    const menu = [];

    if (row.directFromPartner && !row.isGeneratePayment && row.isLoadedPickUp) {
      menu.push({
        key: 'Generate Payment',
        label: <SecondaryLabel className="single-line f-16 w-full">Generate Payment</SecondaryLabel>,
        valueLabel: 'Generate Payment',
      });
    }
    if (row.isInboundIsOutboundTransfer) {
      menu.push({
        key: 'Generate Inbound/Outbound Transfer',
        label: <SecondaryLabel className="single-line f-16 w-full">Generate Inbound/Outbound Transfer</SecondaryLabel>,
        valueLabel: 'Generate Inbound/Outbound Transfer',
      });
    }
    if (row.isDestinationYard && !row.isInboundTransfer && !row.isInboundIsOutboundTransfer) {
      menu.push({
        key: 'Generate Inbound Transfer',
        label: <SecondaryLabel className="single-line f-16 w-full">Generate Inbound Transfer</SecondaryLabel>,
        valueLabel: 'Generate Inbound Transfer',
      });
    }
    if (row.isOriginYard && !row.isOutboundTransfer && !row.isInboundIsOutboundTransfer) {
      menu.push({
        key: 'Generate Outbound Transfer',
        label: <SecondaryLabel className="single-line f-16 w-full">Generate Outbound Transfer</SecondaryLabel>,
        valueLabel: 'Generate Outbound Transfer',
      });
    }
    if ((row.isEmptyPickUp || row.isEmptyDelivered) && !row.receivedAtLocation) {
      menu.push({
        key: 'Received at location',
        label: <SecondaryLabel className="single-line f-16 w-full">Received at location</SecondaryLabel>,
        valueLabel: 'Received at location',
      });
    }
    menu.push(
      {
        key: 'Re-assign carrier',
        label: <SecondaryLabel className="single-line f-16 w-full">Re-assign carrier</SecondaryLabel>,
        valueLabel: 'Re-assign carrier',
      },
      {
        key: 'Edit',
        label: <SecondaryLabel className="single-line f-16 w-full">Edit</SecondaryLabel>,
        valueLabel: 'Edit',
      },
    );

    return menu;
  };

  const colSpan = useMemo(
    () => (tableViewColumns?.data?.length ? tableViewColumns?.data?.length : 0) + 2,
    [tableViewColumns?.data?.length],
  );

  return (
    <div className="h-100 overflow-hidden pb-15">
      {tableViewColumns.progressing ? (
        <div className="h-100 background-white br-4">
          <ContentSpinner />
        </div>
      ) : (
        <ScrollPagination
          className="background-white br-4 w-100 h-100 scrollable-table-container horizontal"
          loadMore={handleLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          scrollOffset={50}
        >
          <table className="w-100 background-white br-4">
            <thead>
              <tr>
                <th
                  className="background-header text-white fixed fixed-top fixed-left"
                  style={{
                    whiteSpace: 'pre-wrap !important',
                  }}
                >
                  Shipment No
                </th>
                {(tableViewColumns?.data ? tableViewColumns.data : []).map(({ columnName, columnValue }) => (
                  <th key={columnValue} className="background-header text-white fixed fixed-top">
                    {columnName}
                  </th>
                ))}
                <th className="background-header text-white fixed fixed-top fixed-right" />
              </tr>
            </thead>
            <tbody>
              {shipmentList.map((row) => (
                <tr key={row.shipmentKey}>
                  <td className="fixed fixed-left">
                    <SecondaryLabel
                      className="cursor-pointer f-18 fw-700 text-header"
                      onClick={() => handleShipmentClick(row.shipmentKey)}
                    >
                      {row.shipmentNo}
                    </SecondaryLabel>
                  </td>
                  {(tableViewColumns?.data ? tableViewColumns.data : []).map(({ columnValue }) => (
                    <GenerateColumnValues
                      key={columnValue}
                      fieldName={columnValue}
                      value={row[columnValue]}
                      row={row}
                    />
                  ))}
                  <td className="pl-5 pr-8 fixed fixed-right text-center">
                    <Dropdown
                      menu={{
                        items: getMenu(row),
                        onClick: ({ key, event }) => {
                          event?.stopPropagation?.();
                          if (key === 'Re-assign carrier') {
                            return;
                          }
                          handleStatusUpdate(row, key);
                        },
                      }}
                      placement="bottomLeft"
                    >
                      <Button
                        ghost
                        className="px-5 min-width-auto bg-none border-none p-0 shadow-none"
                        size="small"
                        shape="circle"
                        type="ghost"
                      >
                        <MoreIcon />
                      </Button>
                    </Dropdown>
                  </td>
                </tr>
              ))}
              {!isLoading && shipmentList?.length === 0 && (
                <tr>
                  <td colSpan={colSpan} className="background-white text-center">
                    <PrimaryLabel className="text-danger">
                      {t('translation:default.notification.emptyList')}.{'  '}
                      {t('translation:default.emptyRecords.title')}.
                    </PrimaryLabel>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </ScrollPagination>
      )}
    </div>
  );
};

const GenerateColumnValues = memo(({ fieldName, value, row }) => {
  const generateFieldValues = (fieldName, value) => {
    switch (fieldName) {
      case 'status':
        return (
          <td key={fieldName}>
            <BatchLabel className="background-statusColor f-15">{value}</BatchLabel>
          </td>
        );
      case 'trailerStatus': {
        const isLoaded = row?.isLoadedPickUp || row?.isLoadedDelivered;
        const color = isLoaded ? '#16a34a' : '#6b7280';
        const title = isLoaded ? 'Loaded' : 'Empty';
        return (
          <td className="text-center" key={fieldName}>
            <div
              style={{
                alignItems: 'center',
                background: isLoaded ? '#E0F2FE' : '#F3F4F6',
                borderRadius: '999px',
                color,
                display: 'inline-flex',
                fontSize: '12px',
                fontWeight: 500,
                gap: 6,
                padding: '4px 10px',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="7" width="18" height="10" rx="3" fill={color} />
              </svg>
              {title}
            </div>
          </td>
        );
      }
      case 'eta':
        return <td key={fieldName}>{dateFormatter(value)}</td>;
      case 'etd':
        return <td key={fieldName}>{dateFormatter(value)}</td>;
      case 'shippingComplete':
      case 'receivingComplete':
        return (
          <td key={fieldName}>
            <Checkbox checked={value} disabled />
          </td>
        );
      default:
        return (
          <td key={fieldName}>
            <SecondaryLabel className="f-15 fw-600 text-black">{value}</SecondaryLabel>
          </td>
        );
    }
  };
  return <>{generateFieldValues(fieldName, value)}</>;
});

export default SaversShipmentGridView;
